import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../../lib/supabaseClient";

const Stepsgoal = () => {
  const TARGET_STEPS = 10000;
  const MAX_STEPS = 15000; // Hard limit set here
  
  const [steps, setSteps] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  const today = new Date().toISOString().split("T")[0];

  // Rotating Pro Tips
  const healthTips = useMemo(() => [
    { title: "Metabolism Boost", text: "A 10-minute walk after meals helps stabilize blood sugar levels.", icon: "🥗" },
    { title: "Joint Health", text: "Consistency is better than intensity. Daily walking keeps joints lubricated.", icon: "🦴" },
    { title: "Mental Clarity", text: "Walking in nature is proven to reduce cortisol (stress) by up to 20%.", icon: "🌲" },
    { title: "Optimal Limit", text: "15,000 steps is an elite level of activity. Ensure you recover well!", icon: "🏆" }
  ], []);

  const currentTip = useMemo(() => healthTips[Math.floor(Math.random() * healthTips.length)], [healthTips]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        setUserId(user.id);

        const { data, error } = await supabase
          .from("daily_activity")
          .select("steps")
          .eq("user_id", user.id)
          .eq("date", today)
          .maybeSingle();

        if (error) throw error;
        if (data) setSteps(Math.min(data.steps, MAX_STEPS)); // Apply limit on load
      } catch (err) {
        console.error("Fetch Error:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [today]);

  const updateSteps = async (delta) => {
    if (!userId) return;
    
    // Strict enforcement of 0 to 15,000
    const newSteps = Math.max(0, Math.min(MAX_STEPS, steps + delta));
    
    // If we are already at max and trying to add more, do nothing
    if (newSteps === steps && delta > 0) return;

    setSteps(newSteps);

    const { error } = await supabase
      .from("daily_activity")
      .upsert({ user_id: userId, date: today, steps: newSteps }, { onConflict: "user_id,date" });

    if (error) {
      console.error("Backend Update Failed:", error);
      setSteps(steps); 
    }
  };

  if (loading) return <div className="h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>;
  if (!userId) return <div style={styles.loader}>Please sign in.</div>;

  const progressPercent = (steps / TARGET_STEPS) * 100;
  const isMaxed = steps >= MAX_STEPS;
  
  // Progress bar visual (caps at 100% full visually even at 150% steps)
  const visualProgress = Math.min(progressPercent, 100);
  
  const calories = Math.round(steps * 0.04);
  const distance = (steps * 0.000762).toFixed(2);

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (visualProgress / 100) * circumference;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <header style={styles.header}>
          <div style={{
            ...styles.badge, 
            background: isMaxed ? "#fee2e2" : (progressPercent >= 100 ? "#dcfce7" : "#e0f2fe"),
            color: isMaxed ? "#991b1b" : (progressPercent >= 100 ? "#166534" : "#075985")
          }}>
            {isMaxed ? "🚀 MAX LIMIT REACHED" : (progressPercent >= 100 ? "⭐ GOAL COMPLETE" : "DAILY TARGET")}
          </div>
          <h2 style={styles.title}>Step Tracker</h2>
          <p style={styles.dateDisplay}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </header>

        <section style={styles.mainCard}>
          <div style={styles.chartContainer}>
            <svg width="180" height="180" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r={radius} stroke="#f1f5f9" strokeWidth="8" fill="none" />
              <circle
                cx="50" cy="50" r={radius}
                stroke={isMaxed ? "#ef4444" : (progressPercent >= 100 ? "#fbbf24" : "#10b981")}
                strokeWidth="8" fill="none"
                strokeDasharray={circumference}
                style={{ strokeDashoffset: offset, transition: "stroke-dashoffset 1s ease-out" }}
                strokeLinecap="round" transform="rotate(-90 50 50)"
              />
            </svg>
            <div style={styles.innerValue}>
              <span style={{
                ...styles.percentageText, 
                color: isMaxed ? "#ef4444" : (progressPercent >= 100 ? "#d97706" : "#0f172a")
              }}>
                {Math.round(progressPercent)}%
              </span>
              <span style={styles.goalText}>{isMaxed ? "Limit Hit" : "of Goal"}</span>
            </div>
          </div>

          <div style={styles.statsGrid}>
            <div style={styles.statBox}>
              <span style={styles.statValue}>{steps.toLocaleString()}</span>
              <span style={styles.statLabel}>Steps</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statValue}>{distance}</span>
              <span style={styles.statLabel}>KM</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statValue}>{calories}</span>
              <span style={styles.statLabel}>Kcal</span>
            </div>
          </div>
        </section>

        <section style={styles.controls}>
          <button style={styles.minusBtn} onClick={() => updateSteps(-500)}>−</button>
          <button 
            disabled={isMaxed}
            style={{
                ...styles.mainBtn, 
                background: isMaxed ? "#94a3b8" : (progressPercent >= 100 ? "#fbbf24" : "#10b981"),
                cursor: isMaxed ? "not-allowed" : "pointer"
            }} 
            onClick={() => updateSteps(1000)}
          >
             {isMaxed ? "Limit Reached" : "+ 1,000 Steps"}
          </button>
          <button style={styles.plusBtn} onClick={() => updateSteps(500)}>+</button>
        </section>

        <div style={styles.tipCard}>
          <div style={styles.tipIcon}>{currentTip.icon}</div>
          <div style={styles.tipContent}>
            <p style={styles.tipTitle}>{currentTip.title}</p>
            <p style={styles.tipText}>{currentTip.text}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
    page: { minHeight: "100vh", background: "#f8fafc", color: "#0f172a", fontFamily: "'Inter', sans-serif", padding: "24px 16px" },
    container: { maxWidth: "400px", margin: "0 auto" },
    loader: { textAlign: "center", paddingTop: "100px", color: "#94a3b8" },
    header: { marginBottom: "28px" },
    badge: { display: "inline-block", padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "700", letterSpacing: "0.5px" },
    title: { margin: "8px 0 2px", fontSize: "24px", fontWeight: "800" },
    dateDisplay: { color: "#64748b", fontSize: "14px" },
    mainCard: { background: "#ffffff", borderRadius: "28px", padding: "32px 20px", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.04)", display: "flex", flexDirection: "column", alignItems: "center" },
    chartContainer: { position: "relative", marginBottom: "28px" },
    innerValue: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "column", alignItems: "center" },
    percentageText: { fontSize: "36px", fontWeight: "800" },
    goalText: { fontSize: "12px", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase" },
    statsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", width: "100%", borderTop: "1px solid #f1f5f9", paddingTop: "24px" },
    statBox: { textAlign: "center" },
    statValue: { display: "block", fontSize: "18px", fontWeight: "700", color: "#334155" },
    statLabel: { fontSize: "10px", color: "#94a3b8", fontWeight: "700", textTransform: "uppercase" },
    controls: { display: "flex", gap: "10px", marginTop: "20px" },
    mainBtn: { flex: 2, color: "white", border: "none", borderRadius: "18px", fontWeight: "700", transition: "all 0.2s", height: "54px" },
    minusBtn: { flex: 1, background: "white", border: "1px solid #e2e8f0", borderRadius: "18px", fontSize: "20px", cursor: "pointer" },
    plusBtn: { flex: 1, background: "white", border: "1px solid #e2e8f0", borderRadius: "18px", fontSize: "20px", cursor: "pointer" },
    tipCard: { marginTop: "24px", display: "flex", gap: "14px", padding: "18px", background: "#ffffff", borderRadius: "20px", border: "1px solid #f1f5f9" },
    tipIcon: { fontSize: "24px", background: "#f8fafc", width: "45px", height: "45px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "14px" },
    tipContent: { flex: 1 },
    tipTitle: { margin: "0 0 2px", fontSize: "14px", fontWeight: "700", color: "#334155" },
    tipText: { margin: 0, fontSize: "12px", color: "#64748b", lineHeight: "1.4" }
};

export default Stepsgoal;